import { ATTENDEE_EMAIL, SPEAKER_EMAIL, SPONSOR_EMAIL } from '@utils/constants';
import {
  AccountPortfolio,
  PublicAttendee,
  PublicEventDetailsResponse,
} from '@hubs101/booking-api-skd-client/lib/types';
import { Document, Image, Link, Page, Text, View } from '@react-pdf/renderer';
import { useCallback, useMemo } from 'react';

import { DefaultTheme } from 'styled-components';
import Hand from '@assets/icons/hand-min.png';
import { ROOT_URL } from '@utils/apiHooks';
import Style from '@react-pdf/types';
import { bookingLink } from '@constants/mappings';
import dayjs from 'dayjs';
import { eventTimeFormat } from '@utils/time';
import { getImageSrc } from '@utils/fn';
import { styles } from './styles';
import { useTranslation } from 'react-i18next';

interface AttendeeListProps {
  event: PublicEventDetailsResponse;
  theme: DefaultTheme;
  account: AccountPortfolio | undefined;
  attendees: PublicAttendee[] | undefined;
}

const AttendeeList = ({
  event,
  theme,
  account,
  attendees,
}: AttendeeListProps) => {
  const { t } = useTranslation();
  const location = useMemo(
    () =>
      [event?.location?.name, event?.location?.marker?.city]
        ?.filter(Boolean)
        .join(', ') ?? '',
    [event?.location?.marker?.city, event?.location?.name]
  );

  const eventDates = useMemo(
    () => eventTimeFormat(event?.start_date, event?.end_date, event?.language),
    [event?.end_date, event?.language, event?.start_date]
  );

  const accountAddress = useMemo(() => {
    return [
      [account?.address?.street],
      [account?.address?.postalCode, account?.address?.city]
        ?.filter(Boolean)
        .join(' '),
      [
        account?.address?.country
          ? t(`countries.${account?.address?.country ?? ''}`)
          : '',
      ],
    ]
      ?.filter(Boolean)
      .join(' | ');
  }, [
    account?.address?.city,
    account?.address?.country,
    account?.address?.postalCode,
    account?.address?.street,
    t,
  ]);

  const Header = useCallback(() => {
    return (
      <View style={styles.header} fixed>
        {event?.images?.logoTransparent?.length > 0 && (
          <Image
            style={styles.logo}
            src={getImageSrc(event?.images?.logoTransparent)}
          />
        )}
        <Text style={{ ...styles.title, color: event?.design?.themeColor }}>
          {event?.name}
        </Text>
        <View style={styles.details}>
          <Text
            style={{ ...styles.location, color: theme.color.mobilityGrey3 }}
          >
            {location}
          </Text>
          <Text
            style={{ ...styles.location, color: theme.color.mobilityGrey3 }}
          >
            {eventDates}
          </Text>
        </View>
      </View>
    );
  }, [
    event?.design?.themeColor,
    event?.images?.logoTransparent,
    event?.name,
    eventDates,
    location,
    theme.color.mobilityGrey3,
  ]);

  const Footer = useCallback(() => {
    return (
      <View style={styles.footer} fixed>
        <View style={styles.companyWrapper}>
          <View style={styles.row}>
            <Text
              style={{ ...styles.boldText, color: theme.color.mobilityGrey3 }}
            >
              {event?.name}
            </Text>
            <Text
              style={{ ...styles.location, color: theme.color.mobilityGrey3 }}
            >
              {t('is-brought-to-you')}
            </Text>
          </View>
          <View style={styles.companyDetails}>
            <Text
              style={{ ...styles.boldText, color: theme.color.mobilityGrey3 }}
            >
              we.CONECT Global Leaders GmbH
            </Text>
            <Text
              style={{ ...styles.location, color: theme.color.mobilityGrey3 }}
            >
              {accountAddress}
            </Text>
            <View style={styles.companyContact}>
              <Link
                style={{ ...styles.link, color: theme.color.mobilityGrey3 }}
                src={`tel:${account?.contact?.phone ?? ''}`}
              >
                {account?.contact?.phone}
              </Link>
              <Text
                style={{ ...styles.location, color: theme.color.mobilityGrey3 }}
              >{`| `}</Text>
              <Link
                style={{ ...styles.link, color: theme.color.mobilityGrey3 }}
                src={`mailto:${account?.contact?.email ?? ''}`}
              >
                {account?.contact?.email}
              </Link>

              <Text
                style={{ ...styles.location, color: theme.color.mobilityGrey3 }}
              >{`| `}</Text>

              <Link
                style={{ ...styles.link, color: theme.color.mobilityGrey3 }}
                src={account?.contact?.website1}
              >
                {account?.contact?.website1}
              </Link>
            </View>
          </View>
        </View>
        <View style={styles.buttonContainer}>
          <Link
            style={{
              ...styles.button,
              backgroundColor: event?.design?.themeColor,
            }}
            src={`${bookingLink[ROOT_URL]}purchase/${event?.slug}`}
          >
            {t('book-now')}
          </Link>
        </View>
        <Image style={styles.hand} src={Hand} />
        <View style={styles.accountLogo}>
          {String(account?.images?.logo)?.length > 0 && (
            <Image
              style={styles.accountLogo}
              src={getImageSrc(account?.images?.logo)}
            />
          )}
        </View>
      </View>
    );
  }, [
    account?.contact?.email,
    account?.contact?.phone,
    account?.contact?.website1,
    account?.images?.logo,
    accountAddress,
    event?.design?.themeColor,
    event?.name,
    event?.slug,
    t,
    theme.color.mobilityGrey3,
  ]);

  const headers = useMemo(
    () => [
      { key: t('company'), width: '30%', paddingLeft: 30 },
      { key: t('job-title'), width: '20%', paddingLeft: 0 },
      { key: t('job-level'), width: '26%', paddingLeft: 0 },
      { key: t('industry'), width: '12%', paddingLeft: 0 },
      { key: t('country'), width: '12%', paddingLeft: 0 },
    ],
    [t]
  );

  const body = useMemo(() => {
    return attendees?.map(attendee => [
      {
        value: attendee.company?.name,
        width: '30%',
        paddingLeft: 30,
        fontWeight: 'bold' as Style.FontWeight,
        fontFamily: 'Lato',
      },
      {
        value: attendee.job?.title,
        width: '20%',
        paddingLeft: 0,
        fontWeight: 'normal' as Style.FontWeight,
        fontFamily: 'Lato',
      },
      {
        value: t(`job-levels.${String(attendee.job?.level)}`),
        width: '26%',
        paddingLeft: 0,
        fontWeight: 'normal' as Style.FontWeight,
        fontFamily: 'Lato',
      },
      {
        value: t(`industries.${String(attendee.company?.industry)}`),
        width: '12%',
        paddingLeft: 0,
        fontWeight: 'normal' as Style.FontWeight,
        fontFamily: 'Lato',
      },
      {
        value: t(`countries.${String(attendee.address?.country)}`),
        width: '12%',
        paddingLeft: 0,
        fontWeight: 'normal' as Style.FontWeight,
        fontFamily: 'Lato',
      },
    ]);
  }, [attendees, t]);

  const footerButtons = useMemo(
    () => [
      {
        title: t('to-sponsor').toUpperCase(),
        email: SPONSOR_EMAIL,
      },
      {
        title: t('to-attend').toUpperCase(),
        email: ATTENDEE_EMAIL,
      },
      {
        title: t('to-speak').toUpperCase(),
        email: SPEAKER_EMAIL,
      },
    ],
    [t]
  );

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={{ paddingBottom: 101 }}>
        <Header />

        <View style={styles.content}>
          <Text
            style={{ ...styles.tableTitle, color: theme.color.mobilityGrey3 }}
          >
            {t('attendee-list')} {dayjs(event?.start_date).format('YYYY')}
          </Text>
          <View style={styles.tableRow} fixed>
            {headers.map(header => (
              <Text
                key={header.key}
                style={[
                  styles.tableHeader,
                  {
                    width: header.width,
                    paddingLeft: header.paddingLeft,
                    borderColor: event?.design?.themeColor,
                  },
                ]}
              >
                {header.key}
              </Text>
            ))}
          </View>
          {body?.map((attendee, i) => {
            const backgroundColor = '#ffffff';
            return (
              <View
                style={{
                  ...styles.tableRow,
                  borderColor: event?.design?.themeColor,
                }}
                key={i}
              >
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: attendee?.[0].width,
                      fontWeight: attendee?.[0].fontWeight,
                      fontFamily: attendee?.[0].fontFamily,
                      paddingLeft: attendee?.[0].paddingLeft,
                      backgroundColor,
                    },
                  ]}
                >
                  {attendee?.[0].value}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: attendee?.[1].width,
                      fontWeight: attendee?.[1].fontWeight,
                      paddingLeft: attendee?.[1].paddingLeft,
                      backgroundColor,
                    },
                  ]}
                >
                  {attendee?.[1].value}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: attendee?.[2].width,
                      fontWeight: attendee?.[2].fontWeight,
                      paddingLeft: attendee?.[2].paddingLeft,
                      backgroundColor,
                    },
                  ]}
                >
                  {attendee?.[2].value}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: attendee?.[3].width,
                      fontWeight: attendee?.[3].fontWeight,
                      paddingLeft: attendee?.[3].paddingLeft,
                      backgroundColor,
                    },
                  ]}
                >
                  {attendee?.[3].value}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    {
                      width: attendee?.[4].width,
                      fontWeight: attendee?.[4].fontWeight,
                      paddingLeft: attendee?.[4].paddingLeft,
                      backgroundColor,
                    },
                  ]}
                >
                  {attendee?.[4].value}
                </Text>
              </View>
            );
          })}
        </View>
        <View style={styles.footerButtonsWrapper} wrap={false}>
          <Text style={styles.buttonsWrapperText}>{t('get-in-touch')}</Text>
          <View style={styles.buttonsWrapper}>
            {footerButtons.map(({ title, email }) => (
              <View key={title} style={styles.footerButton}>
                <Text style={styles.buttonTitleText}>{title}</Text>
                <Link style={styles.buttonLinkText} src={`mailto:${email}`}>
                  {email}
                </Link>
              </View>
            ))}
          </View>
        </View>
        <Footer />
      </Page>
    </Document>
  );
};

export default AttendeeList;
